<template>
  <div>
    <!-- Create company -->
    <CompanyCreateDialog
      v-model:visible="companyCreateDialog"
      @company-created="handleCompanyLoad" />

    <div class="header-wrapper">
      <header>{{ i18n.t('companies') }}</header>
      <div style="cursor: pointer" @click="companyCreateDialog = true">
        <SmTooltip placement="right">
          <Plus
            style="margin-right: -5px"
            :size="18"
            class="text-contrast hover:text-primary" />

          <template #content>
            {{ i18n.t('createCompany') }}
          </template>
        </SmTooltip>
      </div>
    </div>
    <p>{{ i18n.t('chooseCompanyLoginText') }}</p>
    <div class="companies">
      <div
        v-for="company in companies"
        :key="`${company.company_id}-${company.region}`"
        @mouseenter="hoveredCompany = company"
        @mouseleave="hoveredCompany = null"
        @click="loginToCompany(company)">
        <CompanySelectItem :company="company" />
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="companies.length === 0 && !loading">
      <SmButton
        style="margin-top: 1rem"
        size="large"
        @click="companyCreateDialog = true">
        {{ i18n.t('createCompany') }}
      </SmButton>
    </div>

    <!-- Loading state -->
    <div v-if="loading" style="width: 100%; height: 75px">
      <SmLoading :loading="loading" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'

  import {
    AvailableCompany,
    CompaniesService,
    CompanyAccessRole,
    OpenAPI,
  } from '@/client'
  import { useGlobalStore } from '@/stores/globalStore'
  import { useSessionStore } from '@/stores/sessionStore'

  import { Plus } from 'lucide-vue-next'
  import { CacheStateRepository } from '@/stores/cacheState.store'
  import { resetDatabase } from '@/stores/database'

  const i18n = useI18n()
  const sessionStore = useSessionStore()
  const globalStore = useGlobalStore()
  const router = useRouter()

  const loading = ref(false)

  const companyCreateDialog = ref(false)
  type CompanyRegion = 'EU' | 'US'
  type CompanyWithRegion = AvailableCompany & { region: CompanyRegion }

  const companies = ref<CompanyWithRegion[]>([])

  async function getCompanies() {
    loading.value = true
    let companies: CompanyWithRegion[] = []

    if (import.meta.env.MODE === 'production') {
      const euCompanies = await fetchCompaniesByRegion(
        globalStore.productionApi,
        'EU'
      )
      const usCompanies = await fetchCompaniesByRegion(
        globalStore.productionUsApi,
        'US'
      )
      companies = [...euCompanies, ...usCompanies]
    } else {
      const localCompanies = await CompaniesService.getAvailableCompaniesCompaniesGet(
        {}
      )
      companies = addRegionToCompanies(localCompanies, 'EU')
    }

    loading.value = false

    // Sort by last login
    companies.sort((a, b) => {
      if (a.last_login === b.last_login) return 0
      if (a.last_login === null) return 1
      if (b.last_login === null) return -1
      return a.last_login > b.last_login ? -1 : 1
    })

    return companies
  }

  async function fetchCompaniesByRegion(
    baseUrl: string,
    region: CompanyRegion
  ): Promise<CompanyWithRegion[]> {
    const previousBase = OpenAPI.BASE
    OpenAPI.BASE = baseUrl
    try {
      const response = await CompaniesService.getAvailableCompaniesCompaniesGet(
        {}
      )
      return addRegionToCompanies(response, region)
    } catch {
      return []
    } finally {
      OpenAPI.BASE = previousBase
    }
  }

  function addRegionToCompanies(
    list: AvailableCompany[],
    region: CompanyRegion
  ): CompanyWithRegion[] {
    const enriched: CompanyWithRegion[] = []
    for (const company of list) {
      enriched.push({ ...company, region })
    }
    return enriched
  }

  onMounted(() => {
    handleCompanyLoad()
  })

  async function handleCompanyLoad() {
    companies.value = await getCompanies()

    // If there is only one company and query parameter autoLogin is set, login to that company
    if (
      companies.value.length === 1 &&
      router.currentRoute.value.query.autoLogin
    ) {
      await loginToCompany(companies.value[0])
    }
  }

  async function loginToCompany(company: CompanyWithRegion) {
    if (company.role == CompanyAccessRole.SUSPENDED) return

    if (import.meta.env.MODE === 'production') {
      OpenAPI.BASE =
        company.region === 'US'
          ? globalStore.productionUsApi
          : globalStore.productionApi
    } else {
      OpenAPI.BASE = globalStore.getIp
    }
    await sessionStore.loginToCompany(company)
    router.push({ name: 'Dashboard' })
  }

  // Predict company logins and reset the database previously
  const cachedCompany = ref<string | null>()

  onMounted(async () => {
    cachedCompany.value = (await CacheStateRepository.listCacheStates()).find(
      (s) => s.id === 'companyId'
    )?.companyId
  })

  const hoveredCompany = ref<AvailableCompany | null>(null)

  watch(hoveredCompany, async (val) => {
    // reset the DB if hovering a non-cached company
    if (val && cachedCompany.value && val.company_id !== cachedCompany.value) {
      await resetDatabase()
      cachedCompany.value = null
    }
  })
</script>

<style lang="scss" scoped>
  .companies {
    margin-top: 1rem;

    display: flex;
    flex-direction: column;

    max-height: 60vh;
    overflow-y: auto;
  }

  .company {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;

    padding: 10px;

    border-radius: var(--border-radius-base);
    transition: background-color 0.2s ease;

    &:hover {
      background: theme('colors.gray[100]');
    }
  }

  .dark .company:hover {
    background: theme('colors.gray[600]');
  }

  // Header
  header {
    font-size: 2rem;
    font-weight: 600;
  }

  .header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 0.5rem;
  }
</style>
